import { motion } from "framer-motion";
import { Coffee, Dumbbell, Building2, Train } from "lucide-react";
import kingsImage from "@/assets/kings-cross.jpg";
import westfieldImage from "@/assets/westfield.jpg";

const categories = [
  { icon: Coffee, label: "Cafes" },
  { icon: Dumbbell, label: "Fitness Clubs" },
  { icon: Building2, label: "Hotels" },
  { icon: Train, label: "Transit Hubs" },
];

const LocationsSection = () => {
  return (
    <section className="py-24 bg-background relative overflow-hidden">
      <div className="max-w-7xl px-6 mx-auto">
        <div className="grid grid-cols-1 laptop:grid-cols-2 desktop:grid-cols-2 gap-16 items-center">
          {/* Left Column (Text) */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="order-1 laptop:order-none desktop:order-none laptop:py-8 desktop:py-8"
          >
            <h2 className="text-5xl tablet:text-6xl font-funnel font-bold mb-4 text-foreground">
              Silence in the <br />
              <span className="font-cormorant italic font-normal text-primary">chaos.</span>
            </h2>
            <p className="text-lg text-muted-foreground mb-10 max-w-md">
              Find qwip pods across London. Soundproof sanctuary, right where you need it.
            </p>

            <div className="grid grid-cols-2 gap-4 max-w-md">
              {categories.map((cat, i) => (
                <motion.div 
                  key={cat.label}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1, duration: 0.5 }}
                  viewport={{ once: true }}
                  className="p-5 rounded-2xl bg-card hover:bg-card/80 transition-colors border border-border/50 group cursor-pointer"
                >
                  <cat.icon className="w-7 h-7 mb-3 text-primary group-hover:scale-110 transition-transform" strokeWidth={1.5} />
                  <h3 className="text-base font-medium text-foreground">{cat.label}</h3>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Right Column (Location Cards) */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="order-2 laptop:order-none desktop:order-none grid grid-cols-1 gap-6"
          >
            {/* King's Cross Card */}
            <div className="relative aspect-[4/3] rounded-2xl overflow-hidden shadow-xl">
              <img src={kingsImage} alt="King's Cross Station" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-black/10" />
              <div className="absolute bottom-4 left-4 bg-card/95 backdrop-blur-md p-4 rounded-xl max-w-[200px]">
                <h4 className="text-base font-funnel font-bold text-foreground">King's Cross Station</h4>
                <p className="text-sm text-muted-foreground">London</p>
                <div className="mt-2 text-sm font-medium text-foreground">
                  <a href="https://qwip.co.uk/kgx" className="underline hover:opacity-70 transition-opacity">Find out more</a>
                </div>
              </div>
            </div>

            {/* Westfield London Card */}
            <div className="relative aspect-[4/3] rounded-2xl overflow-hidden shadow-xl">
              <img src={westfieldImage} alt="Westfield London White City" className="w-full h-full object-cover" loading="lazy" />
              <div className="absolute inset-0 bg-black/10" />
              <div className="absolute bottom-4 left-4 bg-card/95 backdrop-blur-md p-4 rounded-xl max-w-[200px]">
                <h4 className="text-base font-funnel font-bold text-foreground">Westfield London</h4>
                <p className="text-sm text-muted-foreground">White City, London</p>
                <div className="mt-2 text-sm font-medium text-foreground">
                  <a href="https://qwip.co.uk/westfield" className="underline hover:opacity-70 transition-opacity">Find out more</a>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default LocationsSection;